/// <reference types="@emotion/react/types/css-prop" />
import {
  ContentModule,
  useAnimationStates,
  useShowInstructions,
} from '@backstage-components/base';
import {css} from '@emotion/react';
import {useMachine} from '@xstate/react';
import {isAfter, isBefore, parseISO} from 'date-fns';
import {motion} from 'framer-motion';
import {useSubscription} from 'observable-hooks';
import {FC, useEffect, useMemo} from 'react';
import {ComponentDefinition, SchemaType} from './CountdownDefinition';
import {countdownStatusMachine} from './status-machine';
import {Clock} from './subComponents/Clock';
import {Eyebrow} from './subComponents/Eyebrow';

export type CountdownComponentDefinition = ContentModule<
  'Countdown',
  SchemaType
>;

export const CountdownComponent: FC<CountdownComponentDefinition> = (
  definition
) => {
  const {props, id} = definition;
  const {animationStates} = props;
  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );
  const motionOptions = useAnimationStates(
    observable,
    broadcast,
    animationStates
  );
  const [state, dispatch] = useMachine(countdownStatusMachine, {
    input: {
      broadcast,
      endTime: parseISO(props.endTime),
      timezoneOffset: new Date().getTimezoneOffset(),
    },
  });

  useEffect(() => {
    dispatch({
      type: 'Countdown:setTargetTime',
      meta: {targetTime: props.endTime},
    });
  }, [dispatch, props.endTime]);

  useSubscription(observable, {next: dispatch});

  useEffect(() => {
    // This code is mostly to advance through the state machine in countdown-status-machine.ts
    const now = new Date();
    if (isBefore(now, state.context.endTime) && state.matches('INITIAL')) {
      broadcast({type: 'Countdown:ready', meta: {}});
    } else if (
      isAfter(now, state.context.endTime) &&
      state.matches('INITIAL')
    ) {
      broadcast({type: 'Countdown:complete', meta: {}});
    }
  }, [broadcast, state]);

  const {
    typography = {},
    divider,
    isUnitUnderNum = true,
    isCountdownColumn = false,
    titleText,
    unitLabels = {},
  } = props;

  const hasDaysLeft: boolean = useMemo(
    () => (state.context.remaining.days ?? 0) > 0,
    [state.context.remaining.days]
  );

  const styles = [
    css({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      color: typography.primaryTextColor,
      fontFamily: typography.fontFamily,
    }),
    css`
      ${definition.style}
      ${definition.props.styleAttr}
    `,
  ];

  return (
    <motion.div
      id={id}
      css={styles}
      {...motionOptions}
      className="countdown-wrapper"
    >
      <Eyebrow titleText={titleText} typography={typography} />
      <Clock
        dateParts={{
          days: state.context.remaining.days,
          hours: state.context.remaining.hours,
          minutes: state.context.remaining.minutes,
          seconds: state.context.remaining.seconds,
        }}
        divider={divider}
        hasDaysLeft={hasDaysLeft}
        typography={typography}
        unitLabels={unitLabels}
        isCountdownColumn={isCountdownColumn}
        isUnitUnderNum={isUnitUnderNum}
      />
    </motion.div>
  );
};
